/**
 * Ombraz Product Filter System
 * This script handles the filtering functionality for Ombraz products.
 * It allows users to filter products based on frame styles, gear, frame sizes, and lens options.
 */

let isMobile = window.matchMedia("(max-width: 1199px)").matches;

// If need to debug, set to true
window.OMBRAZ_DEBUG = false;

// Global variable to track frame style check order (most recent first)
let frameStyleCheckOrder = [];

// Global variable to track gear check order (most recent first)
let gearCheckOrder = [];

// Main initialization on DOMContentLoaded
document.addEventListener("DOMContentLoaded", function () {
  // Set isMobile based on current viewport
  isMobile = window.matchMedia("(max-width: 1199px)").matches;

  initializeAccordion();
  initializeFilters();

  // Check URL parameters first, then handle collection-specific filtering
  const urlParams = new URLSearchParams(window.location.search);
  const hasURLFilters =
    urlParams.has("styles") ||
    urlParams.has("sizes") ||
    urlParams.has("lenses") ||
    urlParams.has("colors") ||
    urlParams.has("gear") ||
    urlParams.has("sale") ||
    urlParams.has("bestseller");

  if (hasURLFilters) {
    // If we have URL parameters, prioritize filter restoration
    debugLog("Found URL parameters, attempting filter restoration");

    const filtersRestored = restoreFiltersFromURL();

    if (!filtersRestored) {
      // If restoration failed, fall back to URL-based filtering
      handleURLBasedFiltering();
    }
  } else {
    // No URL parameters, handle size parameter or collection-specific filtering
    const sizeParameterHandled = handleSizeURLParameter();

    if (!sizeParameterHandled) {
      handleURLBasedFiltering();
    }
  }
});

// Update isMobile when window resizes
window.addEventListener("resize", function () {
  isMobile = window.matchMedia("(max-width: 1199px)").matches;
});

function saveFiltersToURL() {
  // Get active filters
  const frameStyles = getActiveFilters("frame-style");
  const frameSizes = getActiveFilters("frame-size");
  const lensOptions = getActiveFilters("lens-option");
  const frameColors = getActiveFilters("frame-color");
  const gearItems = getActiveFilters("gear");
  const saleChecked =
    document.querySelector('.ombraz-sale [data-collection="onsale"]')
      ?.checked || false;
  const bestSellerChecked =
    document.querySelector('.ombraz-best-seller input[type="checkbox"]')
      ?.checked || false;
  const limitedEditionChecked =
    document.querySelector('.ombraz-limited-edition input[type="checkbox"]')
      ?.checked || false;

  // Create URL parameters
  const params = new URLSearchParams();

  // Only add parameters that have values
  if (frameStyles.length > 0) params.set("styles", frameStyles.join(","));
  if (frameSizes.length > 0) params.set("sizes", frameSizes.join(","));
  if (lensOptions.length > 0) params.set("lenses", lensOptions.join(","));
  if (frameColors.length > 0) params.set("colors", frameColors.join(","));
  if (gearItems.length > 0) params.set("gear", gearItems.join(","));
  if (saleChecked) params.set("sale", "true");
  if (bestSellerChecked) params.set("bestseller", "true");
  if (limitedEditionChecked) params.set("limitededition", "true");

  // Update the URL without refreshing
  if (params.toString()) {
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
    debugLog("Filter state saved to URL", params.toString());
  } else {
    // If no filters, remove any query parameters
    window.history.replaceState({}, "", window.location.pathname);
  }
}

// Improved restoreFiltersFromURL function
function restoreFiltersFromURL() {
  debugLog("Starting restoreFiltersFromURL");

  // Get parameters from URL
  const params = new URLSearchParams(window.location.search);
  let hasRestoredFilters = false;

  // Create an object to track which filters we've restored
  const restoredFilters = {
    "frame-style": [],
    "frame-size": [],
    "lens-option": [],
    "frame-color": [],
    gear: [],
  };

  // Helper function to check filter options
  function checkFilter(type, paramName) {
    if (params.has(paramName)) {
      const values = params.get(paramName).split(",");
      values.forEach((value) => {
        const normalizedValue = value.toLowerCase().trim();
        debugLog(`Checking filter ${type} with value ${normalizedValue}`);

        // Add to our tracking object
        restoredFilters[type].push(normalizedValue);

        const checkbox = document.querySelector(
          `input[data-filter-type="${type}"][data-${type}="${normalizedValue}"]`
        );

        if (checkbox) {
          checkbox.checked = true;
          // Open accordion containing this checkbox
          const accordion = checkbox.closest(".ombraz-accordion-item");
          if (accordion) {
            accordion.setAttribute("open", "");
            updateAccordionIcon(accordion, true);
          }
          hasRestoredFilters = true;
          debugLog(`Restored filter ${type}=${normalizedValue}`);
        } else {
          debugLog(`Could not find checkbox for ${type}=${normalizedValue}`);
        }
      });
      return true; // Indicate that we found and processed this parameter
    }
    return false; // Parameter not found
  }

  // Check all filter types
  checkFilter("frame-style", "styles");
  checkFilter("frame-size", "sizes");
  checkFilter("lens-option", "lenses");
  checkFilter("frame-color", "colors");
  checkFilter("gear", "gear");

  // Initialize frame style check order based on restored styles
  if (restoredFilters["frame-style"].length > 0) {
    // Reverse the order since URL params might be in different order, but we'll assume the order in URL represents check order
    frameStyleCheckOrder = [...restoredFilters["frame-style"]].reverse();
    debugLog(
      "Initialized frame style check order from URL:",
      frameStyleCheckOrder
    );
  }

  // Initialize gear check order based on restored gear
  if (restoredFilters["gear"].length > 0) {
    // Reverse the order since URL params might be in different order, but we'll assume the order in URL represents check order
    gearCheckOrder = [...restoredFilters["gear"]].reverse();
    debugLog("Initialized gear check order from URL:", gearCheckOrder);
  }

  // Restore sale checkbox
  if (params.has("sale") && params.get("sale") === "true") {
    const saleCheckbox = document.querySelector(
      '.ombraz-sale [data-collection="onsale"]'
    );
    if (saleCheckbox) {
      saleCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored sale filter");

      // Ensure on-sale section is visible
      const onSaleSection = document.querySelector("[data-onsale-products]");
      if (onSaleSection) {
        onSaleSection.classList.remove("hide-this", "visually-hidden");
      }
    } else {
      debugLog("Could not find sale checkbox");
    }
  }

  // Restore best seller checkbox
  if (params.has("bestseller") && params.get("bestseller") === "true") {
    const bestSellerCheckbox = document.querySelector(
      '.ombraz-best-seller input[type="checkbox"]'
    );
    if (bestSellerCheckbox) {
      bestSellerCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored bestseller filter");
    } else {
      debugLog("Could not find bestseller checkbox");
    }
  }

  // Restore limited edition checkbox
  if (params.has("limitededition") && params.get("limitededition") === "true") {
    const limitedEditionCheckbox = document.querySelector(
      '.ombraz-limited-edition input[type="checkbox"]'
    );
    if (limitedEditionCheckbox) {
      limitedEditionCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored limited edition filter");
    } else {
      debugLog("Could not find limited edition checkbox");
    }
  }

  // If we have restored any filters, apply them with a delay to ensure DOM is ready
  if (hasRestoredFilters) {
    debugLog(
      "Restored filters from URL parameters, applying filters with delay",
      restoredFilters
    );

    // Toggle UI buttons
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Use a longer delay to ensure DOM is fully loaded
    setTimeout(() => {
      // Apply the filters
      const bestSellerChecked =
        document.querySelector('.ombraz-best-seller input[type="checkbox"]')
          ?.checked || false;
      const limitedEditionChecked =
        document.querySelector('.ombraz-limited-edition input[type="checkbox"]')
          ?.checked || false;
      const saleChecked =
        document.querySelector('.ombraz-sale [data-collection="onsale"]')
          ?.checked || false;

      // Force filter application regardless of mobile/desktop state
      if (limitedEditionChecked) {
        handleLimitedEditionFilter(true); // Pass true to force application
      } else if (bestSellerChecked) {
        handleBestSellerFilter(true); // Pass true to force application
      } else if (saleChecked) {
        handleSaleFilter(true); // Pass true to force application
      } else {
        // Apply filters with special handling for multiple filter types
        applyFiltersFromURL(restoredFilters);
      }

      // For mobile, close the filter sidebar if it's open
      if (isMobile) {
        const sidebar = document.getElementById("ombrazSidebar");
        if (sidebar && sidebar.classList.contains("active")) {
          closeTheFilterHederMobile();
        }
      }
    }, 500);

    return true;
  }

  return false;
}

function applyFiltersFromURL(restoredFilters) {
  debugLog(
    "applyFiltersFromURL: Starting filter application with",
    restoredFilters
  );

  // Get product grid and all items
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  const allItems = productGrid
    ? productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
    : [];

  // Hide or show the product items based on filters
  let visibleCount = 0;

  // Check if any filter type is active
  const hasFrameStyleFilters = restoredFilters["frame-style"].length > 0;
  const hasFrameSizeFilters = restoredFilters["frame-size"].length > 0;
  const hasLensOptionFilters = restoredFilters["lens-option"].length > 0;
  const hasFrameColorFilters = restoredFilters["frame-color"].length > 0;
  const hasGearFilters = restoredFilters["gear"].length > 0;

  const anyFilterActive =
    hasFrameStyleFilters ||
    hasFrameSizeFilters ||
    hasLensOptionFilters ||
    hasFrameColorFilters ||
    hasGearFilters;

  debugLog("applyFiltersFromURL: Filter status", {
    hasFrameStyleFilters,
    hasFrameSizeFilters,
    hasLensOptionFilters,
    hasFrameColorFilters,
    hasGearFilters,
    anyFilterActive,
  });

  // Apply filters to each product item
  allItems.forEach((item) => {
    // For debugging purpose
    const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (item.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();
    const itemColor = (item.getAttribute("data-color") || "").toLowerCase();
    const itemLensValue = (
      item.getAttribute("data-lens-value") || ""
    ).toLowerCase();

    // Get available sizes using our helper function
    const itemSizes = getAvailableSizes(item);

    debugLog(`Checking product: ${itemTitle}`, {
      itemStyle,
      itemSizes,
      itemLensValue,
      itemColor,
      itemGear,
    });

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("nose-shield") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose shield") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    // Special handling for gear items when gear filters are active
    if (hasGearFilters) {
      // If this is a gear item, check if it matches the selected gear filters
      if (isGearOrAccessory) {
        const matchesGear = restoredFilters["gear"].some(
          (gearType) =>
            itemGear.includes(gearType) ||
            (gearType === "shields" &&
              (itemTitle.includes("side shields") ||
                itemStyle.includes("shields"))) ||
            (gearType === "nose-shield" &&
              (itemTitle.includes("nose shield") ||
                itemStyle.includes("nose-shield"))) ||
            (gearType === "risers" &&
              (itemTitle.includes("nose pad") ||
                itemTitle.includes("risers") ||
                itemStyle.includes("riser"))) ||
            (gearType === "floatie" && itemTitle.includes("floatie")) ||
            (gearType === "case" && itemTitle.includes("case")) ||
            (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
            (gearType === "sticker" && itemTitle.includes("sticker")) ||
            (gearType === "giftcard" && itemTitle.includes("gift card"))
        );

        if (matchesGear) {
          item.classList.remove("hide-this");
          visibleCount++;
          debugLog(`Showing gear item: ${itemTitle}`);
          return; // Skip other checks, this is a matched gear item
        } else {
          item.classList.add("hide-this");
          debugLog(`Hiding gear item (no match): ${itemTitle}`);
          return; // Skip other checks, this is an unmatched gear item
        }
      } else {
        // This is not a gear item but gear filter is active, hide it
        item.classList.add("hide-this");
        debugLog(`Hiding non-gear item: ${itemTitle}`);
        return;
      }
    }

    // If gear filter is not active but this is a gear item and other filters are active,
    // we'll skip regular product checks as they don't apply to gear
    if (
      isGearOrAccessory &&
      (hasFrameSizeFilters ||
        hasFrameStyleFilters ||
        hasLensOptionFilters ||
        hasFrameColorFilters)
    ) {
      item.classList.add("hide-this");
      debugLog(`Hiding gear item (other filters active): ${itemTitle}`);
      return;
    }

    // For regular products (non-gear items)
    let matchesAllFilters = true;

    // Check frame style filters
    if (hasFrameStyleFilters) {
      if (!restoredFilters["frame-style"].includes(itemStyle)) {
        matchesAllFilters = false;
        debugLog(
          `Style mismatch for ${itemTitle}: ${itemStyle} not in ${restoredFilters["frame-style"]}`
        );
      }
    }

    // Check frame size filters
    if (matchesAllFilters && hasFrameSizeFilters) {
      // Use the helper function to get available sizes
      const itemSizes = getAvailableSizes(item);

      const matchesSize = restoredFilters["frame-size"].some((size) => {
        const lowerSize = size.toLowerCase();

        // Check if any of the item's available sizes match the filter
        return itemSizes.some((availableSize) => {
          const lowerAvailableSize = availableSize.toLowerCase();

          if (lowerSize === "narrow") {
            return lowerAvailableSize.includes("narrow");
          } else if (lowerSize === "regular") {
            return lowerAvailableSize.includes("regular");
          } else if (lowerSize === "xl") {
            return (
              lowerAvailableSize.includes("xl") ||
              lowerAvailableSize.includes("extra large")
            );
          }

          return lowerAvailableSize === lowerSize;
        });
      });

      if (!matchesSize) {
        matchesAllFilters = false;
      }
    }

    // Check lens option filters
    if (matchesAllFilters && hasLensOptionFilters) {
      const matchesLensOption = restoredFilters["lens-option"].some(
        (option) => {
          if (option === "polarized") {
            return (
              itemLensValue.includes("polarized") &&
              !itemLensValue.includes("non-polarized")
            );
          } else if (option === "non-polarized") {
            return itemLensValue.includes("non-polarized");
          } else if (
            option === "blue-blocking" ||
            option === "low light" ||
            option === "low-light"
          ) {
            return (
              itemLensValue.includes("blue-blocking") ||
              itemLensValue.includes("low light") ||
              itemLensValue.includes("low-light")
            );
          }

          return itemLensValue.includes(option);
        }
      );

      if (!matchesLensOption) {
        matchesAllFilters = false;
        debugLog(
          `Lens mismatch for ${itemTitle}: ${itemLensValue} does not match any of ${restoredFilters["lens-option"]}`
        );
      }
    }

    // Check frame color filters
    if (matchesAllFilters && hasFrameColorFilters) {
      if (!restoredFilters["frame-color"].includes(itemColor)) {
        matchesAllFilters = false;
        debugLog(
          `Color mismatch for ${itemTitle}: ${itemColor} not in ${restoredFilters["frame-color"]}`
        );
      }
    }

    // Apply visibility directly without deduplication for regular products
    if (matchesAllFilters) {
      item.classList.remove("hide-this");
      visibleCount++;
      debugLog(`Showing product: ${itemTitle}`);
    } else {
      item.classList.add("hide-this");
      debugLog(`Hiding product (filter mismatch): ${itemTitle}`);
    }
  });

  debugLog(
    `applyFiltersFromURL: Filter applied, ${visibleCount} items visible`
  );

  // Reorder products based on frame style check order if multiple frame styles are checked
  if (
    restoredFilters["frame-style"].length > 1 &&
    frameStyleCheckOrder.length > 1
  ) {
    reorderProductsByFrameStyle(productGrid, restoredFilters["frame-style"]);
  }

  // Update UI states
  if (productGrid) {
    productGrid.classList.toggle("is-filtered-now", anyFilterActive);
  }

  // Count all visible products across all containers for accurate no-results display
  const totalVisibleCount = getTotalVisibleProductCount();
  debugLog(
    `applyFiltersFromURL: Total visible products across all containers: ${totalVisibleCount}`
  );

  // Show or hide "No results" message
  const showNoResults = totalVisibleCount === 0 && anyFilterActive;
  displayNoResultsMessage(showNoResults);

  // Auto-select frame sizes if filter is active
  if (hasFrameSizeFilters) {
    autoSelectFrameSizes(restoredFilters["frame-size"]);
  }
}

/**
 * Auto-selects frame size radio buttons on visible product cards
 * @param {Array} frameSizes - Array of frame sizes to select (e.g., ['narrow', 'xl'])
 */
function autoSelectFrameSizes(frameSizes) {
  if (!frameSizes || frameSizes.length === 0) return;

  debugLog("autoSelectFrameSizes: Selecting sizes", frameSizes);

  // Get all visible product cards
  const visibleCards = document.querySelectorAll(
    ".ombraz-product-v2__grid-item:not(.hide-this)"
  );

  visibleCards.forEach((item) => {
    const card = item.querySelector(".ombraz-product-v2__card");
    if (!card) return;

    // Determine which size to select based on filters
    let sizeToSelect = null;

    // If only one size is in the filter, use that
    if (frameSizes.length === 1) {
      sizeToSelect = frameSizes[0];
    } else {
      // If multiple sizes, check availability and select first available
      for (const size of frameSizes) {
        const isAvailable =
          card.getAttribute(`data-is-${size}-available`) === "true";
        if (isAvailable) {
          sizeToSelect = size;
          break;
        }
      }
    }

    if (!sizeToSelect) return;

    // Find and click the corresponding radio button
    const radioButton = card.querySelector(
      `input[type="radio"][id^="${sizeToSelect}"]`
    );

    if (radioButton && !radioButton.checked) {
      debugLog(`Auto-selecting ${sizeToSelect} for card`, card);
      radioButton.checked = true;
      // Trigger change event to update product info
      radioButton.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });
}

function initializeFilters() {
  const filterToggle = document.getElementById("ombrazFilterHeader");
  if (filterToggle) {
    filterToggle.addEventListener("click", toggleFilter);
  }

  const filterCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"]'
  );
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  const limitedEditionCheckbox = document.querySelector(
    '.ombraz-limited-edition input[type="checkbox"]'
  );
  const productItems = document.querySelectorAll(
    ".ombraz-product-v2__grid-item"
  );
  const saleCheckbox = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  );

  // Hide both buttons initially
  toggleClearButton(false);
  toggleShowResultsButton(false);

  // Add change event listener to Best Seller checkbox
  if (bestSellerCheckbox) {
    bestSellerCheckbox.addEventListener("change", () => {
      // Uncheck all other filter checkboxes
      const otherFilterCheckboxes = document.querySelectorAll(
        '.ombraz-accordion-content input[type="checkbox"]'
      );
      otherFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      // Uncheck sale checkbox if it exists and is checked
      if (saleCheckbox && saleCheckbox.checked) {
        saleCheckbox.checked = false;

        // Also hide sale products
        const saleProducts = document.querySelector("[data-onsale-products]");
        if (saleProducts) {
          saleProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Uncheck limited edition checkbox if checked
      if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
        limitedEditionCheckbox.checked = false;
      }

      // Show Show Results button on mobile (will be hidden later if not needed)
      toggleShowResultsButton(bestSellerCheckbox.checked);

      // Apply filter immediately on both desktop and mobile
      handleBestSellerFilter();

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Add change event listener to Limited Edition checkbox
  if (limitedEditionCheckbox) {
    limitedEditionCheckbox.addEventListener("change", () => {
      // Uncheck sale checkbox if it exists and is checked
      if (saleCheckbox && saleCheckbox.checked) {
        saleCheckbox.checked = false;

        // Also hide sale products
        const saleProducts = document.querySelector("[data-onsale-products]");
        if (saleProducts) {
          saleProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Uncheck best seller checkbox if checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        bestSellerCheckbox.checked = false;

        // Also hide best seller products
        const bestSellerProducts = document.querySelector(
          "[data-custom-products]"
        );
        if (bestSellerProducts) {
          bestSellerProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Check if any other filters are active
      const otherFilterCheckboxes = document.querySelectorAll(
        '.ombraz-accordion-content input[type="checkbox"]'
      );

      // Uncheck all other filter checkboxes
      otherFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      const hasOtherActiveFilters = Array.from(otherFilterCheckboxes).some(
        (checkbox) => checkbox.checked
      );

      // Show Show Results button based on checkbox state and other filters
      toggleShowResultsButton(
        limitedEditionCheckbox.checked || hasOtherActiveFilters
      );
      toggleClearButton(
        limitedEditionCheckbox.checked || hasOtherActiveFilters
      );

      // Apply filter immediately on both desktop and mobile
      handleLimitedEditionFilter();

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Add change event listener to Sale checkbox
  if (saleCheckbox) {
    saleCheckbox.addEventListener("change", () => {
      // Uncheck best seller checkbox if it exists and is checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        bestSellerCheckbox.checked = false;

        // Also hide best seller products
        const bestSellerProducts = document.querySelector(
          "[data-custom-products]"
        );
        if (bestSellerProducts) {
          bestSellerProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Show Show Results button
      toggleShowResultsButton(saleCheckbox.checked);

      // Apply filter immediately on both desktop and mobile
      handleSaleFilter();

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Show all products by default
  if (productItems.length) {
    productItems.forEach((item) => item.classList.remove("hide-this"));
  }

  // Add checkbox change event listener for regular filters (frame style, size, lens, color, gear)
  if (filterCheckboxes.length) {
    filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) => {
        // Uncheck Limited Edition checkbox if checked
        if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
          limitedEditionCheckbox.checked = false;
        }

        // Uncheck Best Seller checkbox if checked
        if (bestSellerCheckbox && bestSellerCheckbox.checked) {
          bestSellerCheckbox.checked = false;

          // IMPORTANT: Hide Best Seller section and show regular grid
          const bestSellerProducts = document.querySelector(
            "[data-custom-products]"
          );
          const regularProductGrid = document.querySelector(
            ".ombraz-product-v2__grid"
          );

          if (bestSellerProducts) {
            bestSellerProducts.classList.add("hide-this", "visually-hidden");
          }

          if (regularProductGrid) {
            regularProductGrid.classList.remove("hide-this", "visually-hidden");
          }
        }

        // If this is a gear filter being checked, uncheck all other filters
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "gear"
        ) {
          // Uncheck all frame style filters
          const frameStyleCheckboxes = document.querySelectorAll(
            'input[data-filter-type="frame-style"]:checked'
          );
          frameStyleCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck all frame size filters
          const frameSizeCheckboxes = document.querySelectorAll(
            'input[data-filter-type="frame-size"]:checked'
          );
          frameSizeCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck all lens option filters
          const lensOptionCheckboxes = document.querySelectorAll(
            'input[data-filter-type="lens-option"]:checked'
          );
          lensOptionCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck all frame color filters
          const frameColorCheckboxes = document.querySelectorAll(
            'input[data-filter-type="frame-color"]:checked'
          );
          frameColorCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });

          // Uncheck sale checkbox
          const saleCheckbox = document.querySelector(
            '.ombraz-sale input[type="checkbox"]'
          );
          if (saleCheckbox && saleCheckbox.checked) {
            saleCheckbox.checked = false;
          }
        }

        // If this is a frame-style filter being checked, update the check order
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "frame-style"
        ) {
          const frameStyleValue = checkbox.getAttribute("data-frame-style");
          if (frameStyleValue) {
            // Remove if already exists (to avoid duplicates)
            frameStyleCheckOrder = frameStyleCheckOrder.filter(
              (style) => style !== frameStyleValue
            );
            // Add to the beginning (most recent first)
            frameStyleCheckOrder.unshift(frameStyleValue);
            debugLog("Updated frame style check order:", frameStyleCheckOrder);
          }
        }

        // If this is a frame-style filter being unchecked, remove from order
        if (
          !checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "frame-style"
        ) {
          const frameStyleValue = checkbox.getAttribute("data-frame-style");
          if (frameStyleValue) {
            frameStyleCheckOrder = frameStyleCheckOrder.filter(
              (style) => style !== frameStyleValue
            );
            debugLog("Removed from frame style check order:", frameStyleValue);
          }
        }

        // If this is a gear filter being checked, update the check order
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "gear"
        ) {
          const gearValue = checkbox.getAttribute("data-gear");
          if (gearValue) {
            // Remove if already exists (to avoid duplicates)
            gearCheckOrder = gearCheckOrder.filter(
              (gear) => gear !== gearValue
            );
            // Add to the beginning (most recent first)
            gearCheckOrder.unshift(gearValue);
            debugLog("Updated gear check order:", gearCheckOrder);
          }
        }

        // If this is a gear filter being unchecked, remove from order
        if (
          !checkbox.checked &&
          checkbox.getAttribute("data-filter-type") === "gear"
        ) {
          const gearValue = checkbox.getAttribute("data-gear");
          if (gearValue) {
            gearCheckOrder = gearCheckOrder.filter(
              (gear) => gear !== gearValue
            );
            debugLog("Removed from gear check order:", gearValue);
          }
        }

        // If this is a non-gear filter being checked, uncheck all gear filters
        if (
          checkbox.checked &&
          checkbox.getAttribute("data-filter-type") !== "gear"
        ) {
          // Uncheck all gear filters
          const gearCheckboxes = document.querySelectorAll(
            'input[data-filter-type="gear"]:checked'
          );
          gearCheckboxes.forEach((cb) => {
            if (cb !== checkbox) cb.checked = false;
          });
        }

        // Get Sale checkbox
        const saleCheckbox = document.querySelector(
          '.ombraz-sale input[type="checkbox"]'
        );

        // If sale is checked when another filter is checked
        if (saleCheckbox && saleCheckbox.checked && checkbox.checked) {
          // Apply both sale and other filters together
          handleSaleFilter();
        } else {
          // Check if any checkbox is checked
          const hasCheckedBoxes = Array.from(filterCheckboxes).some(
            (cb) => cb.checked
          );

          // Show/hide the Clear button based on checked state
          toggleClearButton(hasCheckedBoxes);

          // Apply filters immediately on both desktop and mobile
          applyFilters(event);
        }

        // On mobile, close the filter sidebar after applying filters
        if (isMobile) {
          closeTheFilterHederMobile();
        }

        // Save filter state to URL
        saveFiltersToURL();
      });
    });
  }

  // Modify Best Seller checkbox event listener:
  if (bestSellerCheckbox) {
    bestSellerCheckbox.addEventListener("change", () => {
      // Uncheck all other filter checkboxes
      const otherFilterCheckboxes = document.querySelectorAll(
        '.ombraz-accordion-content input[type="checkbox"]'
      );
      otherFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      // Uncheck sale checkbox if it exists and is checked
      if (saleCheckbox && saleCheckbox.checked) {
        saleCheckbox.checked = false;

        // Also hide sale products
        const saleProducts = document.querySelector("[data-onsale-products]");
        if (saleProducts) {
          saleProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Uncheck limited edition checkbox if checked
      if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
        limitedEditionCheckbox.checked = false;
      }

      // Show Clear button
      toggleClearButton(bestSellerCheckbox.checked);

      // Apply filter immediately on both desktop and mobile
      handleBestSellerFilter();

      // On mobile, close the filter sidebar after applying filters
      if (isMobile) {
        closeTheFilterHederMobile();
      }

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Modify Limited Edition checkbox event listener:
  if (limitedEditionCheckbox) {
    limitedEditionCheckbox.addEventListener("change", () => {
      // Uncheck sale checkbox if it exists and is checked
      if (saleCheckbox && saleCheckbox.checked) {
        saleCheckbox.checked = false;

        // Also hide sale products
        const saleProducts = document.querySelector("[data-onsale-products]");
        if (saleProducts) {
          saleProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Uncheck best seller checkbox if checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        bestSellerCheckbox.checked = false;

        // Also hide best seller products
        const bestSellerProducts = document.querySelector(
          "[data-custom-products]"
        );
        if (bestSellerProducts) {
          bestSellerProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Check if any other filters are active
      const otherFilterCheckboxes = document.querySelectorAll(
        '.ombraz-accordion-content input[type="checkbox"]'
      );

      // Uncheck all other filter checkboxes
      otherFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      const hasOtherActiveFilters = Array.from(otherFilterCheckboxes).some(
        (checkbox) => checkbox.checked
      );

      // Show Clear button based on checkbox state
      toggleClearButton(
        limitedEditionCheckbox.checked || hasOtherActiveFilters
      );

      // Apply filter immediately on both desktop and mobile
      handleLimitedEditionFilter();

      // On mobile, close the filter sidebar after applying filters
      if (isMobile) {
        closeTheFilterHederMobile();
      }

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Modify Sale checkbox event listener:
  if (saleCheckbox) {
    saleCheckbox.addEventListener("change", () => {
      // Uncheck best seller checkbox if it exists and is checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        bestSellerCheckbox.checked = false;

        // Also hide best seller products
        const bestSellerProducts = document.querySelector(
          "[data-custom-products]"
        );
        if (bestSellerProducts) {
          bestSellerProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Show Clear button
      toggleClearButton(saleCheckbox.checked);

      // Apply filter immediately on both desktop and mobile
      handleSaleFilter();

      // On mobile, close the filter sidebar after applying filters
      if (isMobile) {
        closeTheFilterHederMobile();
      }

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // If no filters from URL parameters, check for collection-specific filtering
  if (!restoreFiltersFromURL()) {
    handleURLBasedFiltering();
  }

  // Initialize the accordion
  initializeAccordion();

  // Clear all filters
  clearAllFilters();
}

// Debug helper function to log filter issues to console
function debugLog(message, data = null) {
  if (window.OMBRAZ_DEBUG) {
    if (data) {
      console.log(`OMBRAZ DEBUG: ${message}`, data);
    } else {
      console.log(`OMBRAZ DEBUG: ${message}`);
    }
  }
}

function applyFilters(event) {
  debugLog("applyFilters: Starting filter application");

  // Get all filter inputs
  const filterInputs = document.querySelectorAll("input[data-filter-type]");

  // Check if any filter is checked
  const anyFilterActive = Array.from(filterInputs).some(
    (input) => input.checked
  );

  // Get all checked filter values
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  // Check if any gear filter is active
  const isGearFilterActive = checkedGearItems.length > 0;

  // Check if the on-sale checkbox is checked
  const onSaleChecked =
    document.querySelector('.ombraz-sale [data-collection="onsale"]')
      ?.checked || false;

  debugLog("applyFilters: Active filters", {
    anyFilterActive,
    checkedFrameStyles,
    checkedFrameSizes,
    checkedLensOptions,
    checkedFrameColors,
    checkedGearItems,
    onSaleChecked,
  });

  // Get product grid and all items
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  const allItems = productGrid
    ? productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
    : [];

  // Hide or show the product items based on filters
  let visibleCount = 0;

  allItems.forEach((item) => {
    // For debugging purpose
    const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (item.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("nose-shield") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose shield") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    // Special handling for gear items when gear filters are active
    if (isGearFilterActive) {
      // If this is a gear item, check if it matches the selected gear filters
      if (isGearOrAccessory) {
        const matchesGear = checkedGearItems.some(
          (gearType) =>
            itemGear.includes(gearType) ||
            (gearType === "shields" &&
              (itemTitle.includes("side shields") ||
                itemStyle.includes("shields"))) ||
            (gearType === "nose-shield" &&
              (itemTitle.includes("nose shield") ||
                itemStyle.includes("nose-shield"))) ||
            (gearType === "risers" &&
              (itemTitle.includes("nose pad") ||
                itemTitle.includes("risers") ||
                itemStyle.includes("riser"))) ||
            (gearType === "floatie" && itemTitle.includes("floatie")) ||
            (gearType === "case" && itemTitle.includes("case")) ||
            (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
            (gearType === "sticker" && itemTitle.includes("sticker")) ||
            (gearType === "giftcard" && itemTitle.includes("gift card"))
        );

        if (matchesGear) {
          item.classList.remove("hide-this");
          visibleCount++;
          return; // Skip other checks, this is a matched gear item
        } else {
          item.classList.add("hide-this");
          return; // Skip other checks, this is an unmatched gear item
        }
      } else {
        // This is not a gear item but gear filter is active, hide it
        item.classList.add("hide-this");
        return;
      }
    }

    // If gear filter is not active but this is a gear item and other filters are active,
    // we'll skip regular product checks as they don't apply to gear
    // EXCEPT on the shop-all page where we want to show all products
    const currentPath = window.location.pathname;
    const isShopAllPage = currentPath.includes("/collections/shop-all");

    if (
      !isShopAllPage &&
      isGearOrAccessory &&
      (checkedFrameSizes.length > 0 ||
        checkedFrameStyles.length > 0 ||
        checkedLensOptions.length > 0 ||
        checkedFrameColors.length > 0)
    ) {
      item.classList.add("hide-this");
      return;
    }

    // For regular products (non-gear items)
    let matchesAllFilters = true;

    // Check frame size filters
    if (checkedFrameSizes.length > 0) {
      // Use the helper function to get available sizes
      const itemSizes = getAvailableSizes(item);

      const matchesSize = checkedFrameSizes.some((size) => {
        const lowerSize = size.toLowerCase();

        // Check if any of the item's available sizes match the filter
        return itemSizes.some((availableSize) => {
          const lowerAvailableSize = availableSize.toLowerCase();

          if (lowerSize === "narrow") {
            return lowerAvailableSize.includes("narrow");
          } else if (lowerSize === "regular") {
            return lowerAvailableSize.includes("regular");
          } else if (lowerSize === "xl") {
            return lowerAvailableSize.includes("xl");
          }

          return lowerAvailableSize === lowerSize;
        });
      });

      if (!matchesSize) {
        matchesAllFilters = false;
      }
    }

    // Check frame style filters
    if (matchesAllFilters && checkedFrameStyles.length > 0) {
      const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();
      if (!checkedFrameStyles.includes(itemStyle)) {
        matchesAllFilters = false;
      }
    }

    // Check lens option filters
    if (matchesAllFilters && checkedLensOptions.length > 0) {
      const itemLensValue = (
        item.getAttribute("data-lens-value") || ""
      ).toLowerCase();
      const matchesLensOption = checkedLensOptions.some((option) => {
        if (option === "polarized") {
          return (
            itemLensValue.includes("polarized") &&
            !itemLensValue.includes("non-polarized")
          );
        } else if (option === "non-polarized") {
          return itemLensValue.includes("non-polarized");
        } else if (
          option === "blue-blocking" ||
          option === "low light" ||
          option === "low-light"
        ) {
          return (
            itemLensValue.includes("blue-blocking") ||
            itemLensValue.includes("low light") ||
            itemLensValue.includes("low-light")
          );
        }

        return itemLensValue.includes(option);
      });

      if (!matchesLensOption) {
        matchesAllFilters = false;
      }
    }

    // Check frame color filters
    if (matchesAllFilters && checkedFrameColors.length > 0) {
      const itemColor = (item.getAttribute("data-color") || "").toLowerCase();
      if (!checkedFrameColors.includes(itemColor)) {
        matchesAllFilters = false;
      }
    }

    // Check on-sale filter if active
    if (matchesAllFilters && onSaleChecked) {
      const compareAtPrice = item.querySelector(
        ".ombraz-product-v2__compare-at-price"
      );
      const itemCompareAtPrice = item.getAttribute("data-compare-at-price");

      // If on-sale is checked, only show items with a compare-at price
      if (!compareAtPrice || !itemCompareAtPrice || itemCompareAtPrice === "") {
        matchesAllFilters = false;
      }
    }

    // Apply visibility directly without deduplication for regular products
    if (matchesAllFilters) {
      item.classList.remove("hide-this");
      visibleCount++;
    } else {
      item.classList.add("hide-this");
    }
  });

  debugLog(`applyFilters: Filter applied, ${visibleCount} items visible`);

  // Reorder products based on frame style check order if multiple frame styles are checked
  if (checkedFrameStyles.length > 1 && frameStyleCheckOrder.length > 1) {
    reorderProductsByFrameStyle(productGrid, checkedFrameStyles);
  }

  // Reorder products based on gear check order if multiple gear filters are checked
  if (checkedGearItems.length > 1 && gearCheckOrder.length > 1) {
    reorderProductsByGear(productGrid, checkedGearItems);
  }

  // Update UI states
  if (productGrid) {
    productGrid.classList.toggle("is-filtered-now", anyFilterActive);
  }

  // Count all visible products across all containers for accurate no-results display
  const totalVisibleCount = getTotalVisibleProductCount();
  debugLog(
    `applyFilters: Total visible products across all containers: ${totalVisibleCount}`
  );

  // Show or hide "No results" message
  const showNoResults = totalVisibleCount === 0 && anyFilterActive;
  displayNoResultsMessage(showNoResults);

  // Auto-select frame sizes if filter is active
  if (checkedFrameSizes.length > 0) {
    autoSelectFrameSizes(checkedFrameSizes);
  }

  // Update URL parameters to reflect current filter state
  saveFiltersToURL();
}

/**
 * Reorders visible products based on frame style check order (most recent first)
 * @param {Element} productGrid - The product grid container
 * @param {Array} checkedFrameStyles - Array of checked frame style values
 */
function reorderProductsByFrameStyle(productGrid, checkedFrameStyles) {
  debugLog(
    "Reordering products by frame style check order:",
    frameStyleCheckOrder
  );

  // Get all visible products
  const visibleProducts = Array.from(
    productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item:not(.hide-this)"
    )
  );

  if (visibleProducts.length === 0) {
    debugLog("No visible products to reorder");
    return;
  }

  // Group products by frame style
  const productsByStyle = {};
  checkedFrameStyles.forEach((style) => {
    productsByStyle[style] = [];
  });

  visibleProducts.forEach((product) => {
    const productStyle = (
      product.getAttribute("data-style") || ""
    ).toLowerCase();
    if (productsByStyle[productStyle]) {
      productsByStyle[productStyle].push(product);
    }
  });

  debugLog(
    "Products grouped by style:",
    Object.keys(productsByStyle).map(
      (style) => `${style}: ${productsByStyle[style].length} products`
    )
  );

  // Create new order based on frameStyleCheckOrder
  const reorderedProducts = [];
  frameStyleCheckOrder.forEach((style) => {
    if (productsByStyle[style] && productsByStyle[style].length > 0) {
      reorderedProducts.push(...productsByStyle[style]);
    }
  });

  // Add any remaining products that might not be in the check order (fallback)
  checkedFrameStyles.forEach((style) => {
    if (!frameStyleCheckOrder.includes(style) && productsByStyle[style]) {
      reorderedProducts.push(...productsByStyle[style]);
    }
  });

  debugLog(`Reordering ${reorderedProducts.length} products`);

  // Re-append products in new order
  reorderedProducts.forEach((product) => {
    productGrid.appendChild(product);
  });
}

/**
 * Reorders visible products based on gear check order (most recent first)
 * @param {Element} productGrid - The product grid container
 * @param {Array} checkedGearItems - Array of checked gear values
 */
function reorderProductsByGear(productGrid, checkedGearItems) {
  debugLog("Reordering products by gear check order:", gearCheckOrder);

  // Get all visible products
  const visibleProducts = Array.from(
    productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item:not(.hide-this)"
    )
  );

  if (visibleProducts.length === 0) {
    debugLog("No visible products to reorder");
    return;
  }

  // Group products by gear type
  const productsByGear = {};
  checkedGearItems.forEach((gear) => {
    productsByGear[gear] = [];
  });

  visibleProducts.forEach((product) => {
    const itemTitle = (product.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (product.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (product.getAttribute("data-style") || "").toLowerCase();

    // Determine which gear type this product belongs to
    let productGearType = null;
    for (const gearType of checkedGearItems) {
      if (
        itemGear.includes(gearType) ||
        (gearType === "shields" &&
          (itemTitle.includes("side shields") ||
            itemStyle.includes("shields"))) ||
        (gearType === "nose-shield" &&
          (itemTitle.includes("nose shield") ||
            itemStyle.includes("nose-shield"))) ||
        (gearType === "risers" &&
          (itemTitle.includes("nose pad") ||
            itemTitle.includes("risers") ||
            itemStyle.includes("riser"))) ||
        (gearType === "floatie" && itemTitle.includes("floatie")) ||
        (gearType === "case" && itemTitle.includes("case")) ||
        (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
        (gearType === "sticker" && itemTitle.includes("sticker")) ||
        (gearType === "giftcard" && itemTitle.includes("gift card"))
      ) {
        productGearType = gearType;
        break;
      }
    }

    if (productGearType && productsByGear[productGearType]) {
      productsByGear[productGearType].push(product);
    }
  });

  debugLog(
    "Products grouped by gear:",
    Object.keys(productsByGear).map(
      (gear) => `${gear}: ${productsByGear[gear].length} products`
    )
  );

  // Create new order based on gearCheckOrder
  const reorderedProducts = [];
  gearCheckOrder.forEach((gear) => {
    if (productsByGear[gear] && productsByGear[gear].length > 0) {
      reorderedProducts.push(...productsByGear[gear]);
    }
  });

  // Add any remaining products that might not be in the check order (fallback)
  checkedGearItems.forEach((gear) => {
    if (!gearCheckOrder.includes(gear) && productsByGear[gear]) {
      reorderedProducts.push(...productsByGear[gear]);
    }
  });

  debugLog(`Reordering ${reorderedProducts.length} products by gear`);

  // Re-append products in new order
  reorderedProducts.forEach((product) => {
    productGrid.appendChild(product);
  });
}

/**
 * Reorders all products by type on shop-all page (frames first, then gear/accessories)
 * @param {Element} productGrid - The product grid container
 */
function reorderProductsByType(productGrid) {
  debugLog(
    "Reordering products by type (frames first, then gear) on shop-all page"
  );

  // Get all products in the grid
  const allProducts = Array.from(
    productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
  );

  if (allProducts.length === 0) {
    debugLog("No products to reorder by type");
    return;
  }

  // Separate products into frames and gear/accessories
  const frameProducts = [];
  const gearProducts = [];

  allProducts.forEach((product) => {
    const itemTitle = (product.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (product.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (product.getAttribute("data-style") || "").toLowerCase();

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("nose-shield") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose shield") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    if (isGearOrAccessory) {
      gearProducts.push(product);
    } else {
      frameProducts.push(product);
    }
  });

  debugLog(
    `Reordering by type: ${frameProducts.length} frames, ${gearProducts.length} gear items`
  );

  // Combine arrays: frames first, then gear
  const reorderedProducts = [...frameProducts, ...gearProducts];

  // Re-append products in new order
  reorderedProducts.forEach((product) => {
    productGrid.appendChild(product);
  });

  debugLog("Products reordered by type on shop-all page");
}

// Helper function to get active filters by type
function getActiveFilters(filterType) {
  const attributeName = `data-${filterType}`;

  // Get all checked checkboxes for this filter type
  const checkedCheckboxes = document.querySelectorAll(
    `input[data-filter-type="${filterType}"]:checked`
  );

  // Map to get the filter values
  return Array.from(checkedCheckboxes)
    .map((cb) => {
      // Get the value from the appropriate data attribute
      const value = cb.getAttribute(attributeName);
      return value ? value.toLowerCase() : "";
    })
    .filter((value) => value !== ""); // Remove any empty values
}

// Helper function to count all visible products across all containers
function getTotalVisibleProductCount() {
  let totalCount = 0;

  // If a gear filter is active, ignore non-gear containers to avoid false positives
  const gearFiltersActive = getActiveFilters("gear").length > 0;

  // Count visible products in regular product grid
  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  if (
    regularProductGrid &&
    !regularProductGrid.classList.contains("hide-this")
  ) {
    const regularProducts = regularProductGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item:not(.hide-this)"
    );
    totalCount += regularProducts.length;
    debugLog(
      `Regular product grid visible products: ${regularProducts.length}`
    );
  }

  // Count visible products in best seller container
  if (!gearFiltersActive) {
    const bestSellerContainer = document.querySelector(
      "[data-custom-products]"
    );
    if (
      bestSellerContainer &&
      !bestSellerContainer.classList.contains("hide-this")
    ) {
      const bestSellerProducts = bestSellerContainer.querySelectorAll(
        ".bs-product-card-v2__list-item:not(.hide-this)"
      );
      totalCount += bestSellerProducts.length;
      debugLog(
        `Best seller container visible products: ${bestSellerProducts.length}`
      );
    }
  }

  // Count visible products in sale products container
  if (!gearFiltersActive) {
    const saleProductsContainer = document.querySelector(
      "[data-onsale-products]"
    );
    if (
      saleProductsContainer &&
      !saleProductsContainer.classList.contains("hide-this")
    ) {
      const saleProducts = saleProductsContainer.querySelectorAll(
        ".is-onsale-product-card:not(.hide-this)"
      );
      totalCount += saleProducts.length;
      debugLog(
        `Sale products container visible products: ${saleProducts.length}`
      );
    }
  }

  debugLog(`Total visible products across all containers: ${totalCount}`);

  // Safety check: if we have visible products but a "no results" message is showing, force hide it
  if (totalCount > 0) {
    const noResultsMessage = document.getElementById("noResultsMessage");
    if (noResultsMessage && !noResultsMessage.classList.contains("hide-this")) {
      debugLog(
        "SAFETY: Force hiding 'no results' message because products are visible"
      );
      noResultsMessage.classList.add("hide-this");
    }
  }

  return totalCount;
}

// Function to display "No Results" message
// Debounced version of displayNoResultsMessage to prevent flashing
let displayNoResultsTimeout;
let lastFilterState = null;

function displayNoResultsMessage(show) {
  debugLog(`displayNoResultsMessage called with show=${show}`);

  // Clear any existing timeout to prevent conflicts
  if (displayNoResultsTimeout) {
    clearTimeout(displayNoResultsTimeout);
    displayNoResultsTimeout = null;
  }

  if (show) {
    const actualVisibleCount = getTotalVisibleProductCount();
    debugLog(
      `Final check before showing message: ${actualVisibleCount} products visible`
    );

    if (actualVisibleCount === 0) {
      showNoResultsMessage(true);
    } else {
      debugLog(
        `Preventing false positive: ${actualVisibleCount} products are actually visible`
      );
    }
  } else {
    const actualVisibleCount = getTotalVisibleProductCount();
    if (actualVisibleCount === 0) {
      debugLog(
        "Skip hiding 'no results' message because no products are visible"
      );
      return;
    }
    displayNoResultsTimeout = setTimeout(() => {
      showNoResultsMessage(false);
      displayNoResultsTimeout = null;
    }, 50);
  }
}

// Internal function to actually show/hide the message
function showNoResultsMessage(show = true) {
  debugLog(`showNoResultsMessage called with show=${show}`);

  // Find the appropriate container to show the message in
  const container = findVisibleContainer();
  let messageElement = document.getElementById("noResultsMessage");

  if (show) {
    debugLog("Creating or showing No Results message");
    if (!messageElement) {
      // Create the message element if it doesn't exist
      messageElement = document.createElement("div");
      messageElement.id = "noResultsMessage";
      messageElement.className = "ombraz-no-results-message";

      messageElement.innerHTML = `
        <div class="ombraz-no-results-container" role="alert" aria-live="assertive">
          <svg class="hidden" aria-label="hidden" width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M15 9L9 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h2 class="ob-h2">Oh no, big sad!</h2>
          <p class="ob-body">No products match your selected filters :'(
          Adjust the filters to uncover more results</p>
          <button id="clearFiltersBtn" class="ombraz-clear-filters-btn ob-body">Clear All Filters</button>
        </div>
      `;

      // Append to the appropriate container
      if (container) {
        debugLog(
          `Appending No Results message to container: ${container.className}`
        );
        container.appendChild(messageElement);

        // Add event listener to the Clear Filters button
        const clearBtn = messageElement.querySelector("#clearFiltersBtn");
        if (clearBtn) {
          clearBtn.addEventListener("click", function () {
            // Simulate a click on the existing Clear All button
            const clearAllBtn =
              document.getElementById("clear-all") ||
              document.getElementById("clear-all-mobile");
            if (clearAllBtn) {
              clearAllBtn.click();
            }
          });
        }
      } else {
        debugLog("No container found to append No Results message");
      }
    } else {
      // If the message already exists, make sure it's in the correct container
      if (container && messageElement.parentElement !== container) {
        container.appendChild(messageElement);
      }
      messageElement.classList.remove("hide-this");
    }
  } else if (messageElement) {
    // Hide the message if it exists
    debugLog("Hiding No Results message");
    messageElement.classList.add("hide-this");
  }
}

// Helper function to find the appropriate container to show the "no results" message
function findVisibleContainer() {
  debugLog("Finding visible container for No Results message");

  const gearFiltersActive = getActiveFilters("gear").length > 0;
  const onSaleChecked =
    document.querySelector('.ombraz-sale [data-collection="onsale"]')
      ?.checked || false;
  const bestSellerChecked =
    document.querySelector('.ombraz-best-seller input[type="checkbox"]')
      ?.checked || false;

  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const saleProductsContainer = document.querySelector(
    "[data-onsale-products]"
  );
  const bestSellerContainer = document.querySelector("[data-custom-products]");

  if (gearFiltersActive && regularProductGrid) {
    debugLog("Gear filters active, using regular product grid for No Results");
    return regularProductGrid;
  }

  // Check which container is currently visible
  if (
    bestSellerChecked &&
    bestSellerContainer &&
    !bestSellerContainer.classList.contains("hide-this")
  ) {
    debugLog("Using best seller container for No Results message");
    return bestSellerContainer;
  } else if (
    onSaleChecked &&
    saleProductsContainer &&
    !saleProductsContainer.classList.contains("hide-this")
  ) {
    debugLog("Using sale products container for No Results message");
    return saleProductsContainer;
  } else if (
    regularProductGrid &&
    !regularProductGrid.classList.contains("hide-this")
  ) {
    debugLog("Using regular product grid for No Results message");
    return regularProductGrid;
  }

  // Fallback to the first available container in case none are visible
  return regularProductGrid || saleProductsContainer || bestSellerContainer;
}

// Helper function to check if an element is fully visible in viewport
function isElementInViewport(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;

  // Consider element visible if at least 200px of it is in the viewport
  // This provides a better UX by not scrolling when user can already see enough of the content
  const visibleHeight =
    Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
  return visibleHeight >= 200;
}

function uncheckFilters(filterType) {
  const checkboxes = document.querySelectorAll(
    `.ombraz-accordion-content input[type="checkbox"][data-filter-type="${filterType}"]`
  );
  checkboxes.forEach((checkbox) => (checkbox.checked = false));
}

function uncheckLensOptions() {
  const lensOptionCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"][data-filter-type="lens-option"]'
  );
  lensOptionCheckboxes.forEach((checkbox) => (checkbox.checked = false));
}

function getCheckedValues(filterType) {
  const checkedCheckboxes = document.querySelectorAll(
    `.ombraz-accordion-content input[type="checkbox"][data-filter-type="${filterType}"]:checked`
  );

  // Use the right attribute name based on filter type
  let attributeName;
  if (filterType === "frame-size") {
    attributeName = "data-frame-size";
  } else if (filterType === "gear") {
    attributeName = "data-gear";
  } else if (filterType === "frame-color") {
    attributeName = "data-frame-color";
  } else if (filterType === "lens-option") {
    attributeName = "data-lens-option";
  } else {
    attributeName = "data-frame-style";
  }

  return Array.from(checkedCheckboxes).map((cb) =>
    (cb.getAttribute(attributeName) || "").toLowerCase()
  );
}

function getAvailableSizes(item) {
  const sizes = [];

  // First check data attributes (best method)
  const sizeAttr = item.getAttribute("data-sizes");
  if (sizeAttr) {
    return sizeAttr
      .toLowerCase()
      .split(",")
      .map((s) => s.trim());
  }

  // Check data-size attribute
  const sizeSingle = item.getAttribute("data-size");
  if (sizeSingle) {
    sizes.push(sizeSingle.toLowerCase().trim());
    return sizes;
  }

  // Check for variant selectors with size information
  const sizeSelectors = item.querySelectorAll(".variant-size-selector");
  if (sizeSelectors && sizeSelectors.length > 0) {
    sizeSelectors.forEach((selector) => {
      const sizeValue = selector.getAttribute("data-value");
      if (sizeValue) {
        sizes.push(sizeValue.toLowerCase().trim());
      }
    });

    if (sizes.length > 0) {
      return sizes;
    }
  }

  // Fallback to checking input elements
  if (
    item.querySelector('input[id^="regular-"]') ||
    item.querySelector('[data-size="regular"]')
  ) {
    sizes.push("regular");
  }

  if (
    item.querySelector('input[id^="narrow-"]') ||
    item.querySelector('[data-size="narrow"]')
  ) {
    sizes.push("narrow");
  }

  if (
    item.querySelector('input[id^="xl-"]') ||
    item.querySelector('[data-size="xl"]')
  ) {
    sizes.push("xl");
  }

  return sizes;
}

function handleURLBasedFiltering() {
  // Check for URL parameters related to filtering
  const urlParams = new URLSearchParams(window.location.search);
  const currentPath = window.location.pathname;

  // Hide on-sale products by default on all pages except on-sale collection
  const isSalePage =
    currentPath.includes("/collections/onsale") ||
    currentPath.includes("/collections/on-sale");
  const onSaleSection = document.querySelector("[data-onsale-products]");
  const saleCheckbox = document.querySelector(
    '.ombraz-sale [data-collection="onsale"]'
  );

  // First check for URL parameters that should override collection defaults
  const hasURLFilters =
    urlParams.has("styles") ||
    urlParams.has("sizes") ||
    urlParams.has("lenses") ||
    urlParams.has("colors") ||
    urlParams.has("gear") ||
    urlParams.has("sale") ||
    urlParams.has("bestseller");

  // If we have URL parameters, prioritize those over collection-specific logic
  if (hasURLFilters) {
    debugLog("Found URL parameters, applying filter restoration");

    // For sale collection pages with URL parameters, ensure sale section is visible
    if (isSalePage || urlParams.has("sale")) {
      if (onSaleSection) {
        onSaleSection.classList.remove("hide-this", "visually-hidden");
      }
      if (saleCheckbox) {
        saleCheckbox.checked = true;
      }
    }

    // Apply URL parameter restoration
    const filtersRestored = restoreFiltersFromURL();

    if (filtersRestored) {
      return; // Exit early since we've handled URL parameters
    }
  }

  // Handle collection-specific defaults only if no URL parameters were found
  if (onSaleSection && !isSalePage) {
    // Hide the on-sale products section on non-sale pages
    onSaleSection.classList.add("hide-this", "visually-hidden");

    // Ensure the sale checkbox is unchecked
    if (saleCheckbox) {
      saleCheckbox.checked = false;
    }
  }

  // First check for the sizes parameter which can contain comma-separated values
  if (urlParams.has("sizes")) {
    const sizeValues = urlParams.get("sizes").split(",");
    debugLog("Found sizes parameter with values:", sizeValues);

    // Apply each size filter
    sizeValues.forEach((size) => {
      // Check if the size is valid
      if (["narrow", "regular", "xl"].includes(size.toLowerCase())) {
        queryFilterBySize(size.toLowerCase());
      }
    });

    // Show filter UI elements
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Apply filters immediately
    applyFilters();

    return; // Exit early since we've handled the sizes parameter
  }

  // Continue with the rest of the function for collection paths
  if (isSalePage) {
    if (saleCheckbox) {
      saleCheckbox.checked = true;

      // Show on-sale products section
      if (onSaleSection) {
        onSaleSection.classList.remove("hide-this", "visually-hidden");
      }

      // For mobile, always apply the filter immediately when directly on sale page
      handleSaleFilter(true);

      // Show buttons
      toggleShowResultsButton(true);
      toggleClearButton(true);
    }
  } else if (currentPath.includes("/collections/best-sellers")) {
    // Hide on-sale products on best-sellers page
    if (onSaleSection) {
      onSaleSection.classList.add("hide-this", "visually-hidden");
    }

    setBestSellerFilter();
  } else if (currentPath.includes("/collections/non-polarized")) {
    setFilterOption("lens-option", "non-polarized");
  } else if (currentPath.includes("/collections/polarized")) {
    setFilterOption("lens-option", "polarized");
  } else if (currentPath.includes("/collections/blue-blocker")) {
    setFilterOption("lens-option", "low-light");
  } else if (currentPath.includes("/collections/low-light")) {
    setFilterOption("lens-option", "low-light");
  } else if (currentPath.includes("/collections/gear")) {
    if (urlParams.has("sideshields")) {
      setFilterOption("gear", "shields");
    } else if (urlParams.has("noseshields") || urlParams.has("nose-shield")) {
      setFilterOption("gear", "nose-shield");
    } else if (urlParams.has("risers")) {
      setFilterOption("gear", "risers");
    } else {
      setAllGearOptions();
    }
  } else {
    // Check if any size parameter exists in the old format
    if (urlParams.has("narrow")) {
      queryFilterBySize("narrow");
    } else if (urlParams.has("regular")) {
      queryFilterBySize("regular");
    } else if (urlParams.has("xl")) {
      queryFilterBySize("xl");
    }

    // Check for frame style parameters
    if (urlParams.has("classic")) {
      setFilterOption("frame-style", "classic");
    } else if (urlParams.has("dolomite")) {
      setFilterOption("frame-style", "dolomite");
    } else if (urlParams.has("leggero")) {
      setFilterOption("frame-style", "leggero");
    } else if (urlParams.has("cammina")) {
      setFilterOption("frame-style", "cammina");
    } else if (urlParams.has("viale")) {
      setFilterOption("frame-style", "viale");
    } else if (urlParams.has("teton")) {
      setFilterOption("frame-style", "teton");
    }

    // Check for gear parameters
    if (urlParams.has("shields")) {
      setFilterOption("gear", "shields");
    } else if (urlParams.has("nose-shield") || urlParams.has("noseshields")) {
      setFilterOption("gear", "nose-shield");
    } else if (urlParams.has("risers")) {
      setFilterOption("gear", "risers");
    } else if (urlParams.has("floatie")) {
      setFilterOption("gear", "floatie");
    } else if (urlParams.has("case")) {
      setFilterOption("gear", "case");
    } else if (urlParams.has("cordreplacement")) {
      setFilterOption("gear", "cordreplacement");
    } else if (urlParams.has("sticker")) {
      setFilterOption("gear", "sticker");
    } else if (urlParams.has("giftcard")) {
      setFilterOption("gear", "giftcard");
    }

    // Check for lens options
    if (urlParams.has("polarized")) {
      setFilterOption("lens-option", "polarized");
    } else if (urlParams.has("non-polarized")) {
      setFilterOption("lens-option", "non-polarized");
    } else if (urlParams.has("blue-blocking") || urlParams.has("low-light")) {
      setFilterOption("lens-option", "blue-blocking");
      setFilterOption("lens-option", "low-light");
    }

    // Check for frame color
    if (urlParams.has("charcoal")) {
      setFilterOption("frame-color", "charcoal");
    } else if (urlParams.has("dusk")) {
      setFilterOption("frame-color", "dusk");
    } else if (urlParams.has("honey")) {
      setFilterOption("frame-color", "honey");
    } else if (urlParams.has("matte-brown")) {
      setFilterOption("frame-color", "matte-brown");
    } else if (urlParams.has("tortoise")) {
      setFilterOption("frame-color", "Tortoise");
    }

    // Check for best-seller and apply if needed
    if (urlParams.has("best-sellers")) {
      setBestSellerFilter();
    }

    // Check for on-sale filter
    if (urlParams.has("onsale")) {
      if (saleCheckbox) {
        saleCheckbox.checked = true;
        handleSaleFilter(true);
      }
    }
  }

  // If any filter is active, make sure the filter UI matches
  const hasActiveFilters =
    urlParams.has("narrow") ||
    urlParams.has("regular") ||
    urlParams.has("xl") ||
    urlParams.has("sizes") ||
    urlParams.has("styles") || // Add this
    urlParams.has("classic") ||
    urlParams.has("dolomite") ||
    urlParams.has("leggero") ||
    urlParams.has("cammina") ||
    urlParams.has("viale") ||
    urlParams.has("teton") ||
    urlParams.has("shields") ||
    urlParams.has("risers") ||
    urlParams.has("floatie") ||
    urlParams.has("case") ||
    urlParams.has("cordreplacement") ||
    urlParams.has("sticker") ||
    urlParams.has("giftcard") ||
    urlParams.has("polarized") ||
    urlParams.has("non-polarized") ||
    urlParams.has("blue-blocking") ||
    urlParams.has("low-light") ||
    urlParams.has("charcoal") ||
    urlParams.has("dusk") ||
    urlParams.has("honey") ||
    urlParams.has("matte-brown") ||
    urlParams.has("tortoise") ||
    urlParams.has("best-sellers") ||
    urlParams.has("onsale") ||
    currentPath.includes("/collections/on-sale") ||
    currentPath.includes("/collections/onsale") ||
    currentPath.includes("/collections/best-sellers") ||
    currentPath.includes("/collections/non-polarized") ||
    currentPath.includes("/collections/polarized") ||
    currentPath.includes("/collections/blue-blocker") ||
    currentPath.includes("/collections/gear");

  if (hasActiveFilters) {
    // Ensure the filter buttons show correctly
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Apply filters immediately for mobile
    if (isMobile) {
      applyFilters();
    }
  }
}

function setBestSellerFilter() {
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  if (bestSellerCheckbox) {
    bestSellerCheckbox.checked = true;
    handleBestSellerFilter();
  }
}

function setFilterOption(filterType, optionValue) {
  let attributeName;
  if (filterType === "frame-size") {
    attributeName = "data-frame-size";
  } else if (filterType === "gear") {
    attributeName = "data-gear";
  } else if (filterType === "frame-color") {
    attributeName = "data-frame-color";
  } else if (filterType === "lens-option") {
    attributeName = "data-lens-option";
  } else {
    attributeName = "data-frame-style";
  }

  const checkbox = document.querySelector(
    `input[type="checkbox"][data-filter-type="${filterType}"][${attributeName}="${optionValue}"]`
  );

  if (checkbox) {
    const details = checkbox.closest(".ombraz-accordion-item");
    if (details) {
      details.setAttribute("open", "");
      updateAccordionIcon(details, true);
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change"));

      // Show buttons on mobile
      if (isMobile) {
        toggleShowResultsButton(true);
        toggleClearButton(true);
      }
    }
  }
}

function setAllGearOptions() {
  const gearCheckboxes = document.querySelectorAll(
    'input[type="checkbox"][data-filter-type="gear"]'
  );
  const gearDetails = gearCheckboxes[0]?.closest(".ombraz-accordion-item");
  if (gearDetails) {
    gearDetails.setAttribute("open", "");
    updateAccordionIcon(gearDetails, true);
    gearCheckboxes.forEach((checkbox) => {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change"));
    });

    // Show buttons on mobile
    if (isMobile) {
      toggleShowResultsButton(true);
      toggleClearButton(true);
    }
  }
}

function updateAccordionIcon(accordionItem, isOpen) {
  const icon = accordionItem.querySelector(".ombraz-icon");
  if (icon) {
    if (isOpen) {
      icon.classList.remove("ombraz-icon-plus");
      icon.classList.add("ombraz-icon-minus");
    } else {
      icon.classList.remove("ombraz-icon-minus");
      icon.classList.add("ombraz-icon-plus");
    }
  }
}

function initializeAccordion() {
  const accordionItems = document.querySelectorAll(".ombraz-accordion-item");

  accordionItems.forEach((item) => {
    const summary = item.querySelector("summary");
    const icon = summary?.querySelector(".ombraz-icon");

    if (summary) {
      // Set initial icon state
      if (icon) {
        const isOpen = item.hasAttribute("open");
        icon.classList.toggle("ombraz-icon-minus", isOpen);
        icon.classList.toggle("ombraz-icon-plus", !isOpen);
      }

      summary.addEventListener("click", (e) => {
        e.preventDefault();
        const isOpen = item.hasAttribute("open");

        // Close all other accordion items
        accordionItems.forEach((otherItem) => {
          if (otherItem !== item && otherItem.hasAttribute("open")) {
            otherItem.removeAttribute("open");
            const otherIcon = otherItem.querySelector(".ombraz-icon");
            if (otherIcon) {
              otherIcon.classList.remove("ombraz-icon-minus");
              otherIcon.classList.add("ombraz-icon-plus");
            }
          }
        });

        // Toggle current accordion
        item.toggleAttribute("open");

        // Update icon
        if (icon) {
          if (!isOpen) {
            icon.classList.remove("ombraz-icon-plus");
            icon.classList.add("ombraz-icon-minus");
          } else {
            icon.classList.remove("ombraz-icon-minus");
            icon.classList.add("ombraz-icon-plus");
          }
        }
      });
    }
  });
}

function clearAllFilters() {
  const clearButtonDesktop = document.getElementById("clear-all");
  const clearButtonMobile = document.getElementById("clear-all-mobile");

  function handleClearAll() {
    const allCheckboxes = document.querySelectorAll(
      '.ombraz-accordion-content input[type="checkbox"]:checked, .ombraz-best-seller input[type="checkbox"]:checked, .ombraz-limited-edition input[type="checkbox"]:checked, .ombraz-sale input[type="checkbox"]:checked'
    );

    // Get current URL path to check if we're on a collection page
    const currentPath = window.location.pathname;
    const isOnCollectionPage =
      currentPath.includes("/collections/polarized") ||
      currentPath.includes("/collections/non-polarized") ||
      currentPath.includes("/collections/blue-blocker");

    // Get sale checkbox, best seller checkbox, and limited edition checkbox
    const saleCheckbox = document.querySelector(
      '.ombraz-sale input[type="checkbox"]'
    );
    const bestSellerCheckbox = document.querySelector(
      '.ombraz-best-seller input[type="checkbox"]'
    );
    const limitedEditionCheckbox = document.querySelector(
      '.ombraz-limited-edition input[type="checkbox"]'
    );
    const regularProductGrid = document.querySelector(
      ".ombraz-product-v2__grid"
    );
    const saleProducts = document.querySelector("[data-onsale-products]");
    const bestSellerProducts = document.querySelector("[data-custom-products]");

    if (allCheckboxes.length) {
      allCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change"));
      });
    }

    // Clear Best Seller filter
    if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      bestSellerCheckbox.checked = false;
      handleBestSellerFilter();
    }

    // Clear Limited Edition filter
    if (limitedEditionCheckbox && limitedEditionCheckbox.checked) {
      limitedEditionCheckbox.checked = false;
      handleLimitedEditionFilter();
    }

    // Clear Sale filter
    if (saleCheckbox && saleCheckbox.checked) {
      saleCheckbox.checked = false;
      handleSaleFilter();
    }

    // Show regular product grid and hide special sections
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this");
      regularProductGrid.classList.remove("visually-hidden");

      // Show all products in regular grid
      const regularProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item"
      );
      regularProducts.forEach((product) => {
        product.classList.remove("hide-this");
        product.classList.remove("hide-this-by-filter");
      });
    }

    // Hide sale products section
    if (saleProducts) {
      saleProducts.classList.add("hide-this");
      saleProducts.classList.add("visually-hidden");
    }

    // Hide best seller products section
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this");
    }

    // Hide both buttons
    toggleClearButton(false);
    toggleShowResultsButton(false);

    // Clear URL parameters without page reload
    window.history.replaceState({}, "", window.location.pathname);

    // Clear frame style check order
    frameStyleCheckOrder = [];
    debugLog("Cleared frame style check order");

    // Clear gear check order
    gearCheckOrder = [];
    debugLog("Cleared gear check order");

    // If we're on the shop-all page, reorder products by type (frames first, then gear)
    const isShopAllPage = currentPath.includes("/collections/shop-all");
    if (isShopAllPage && regularProductGrid) {
      reorderProductsByType(regularProductGrid);
    }

    // If we're on a special lens collection page, show those items by their lens type
    if (isOnCollectionPage) {
      // Reset any hiding that might have been applied
      resetAllProductVisibility();

      // Find the lens option that should be visible based on the collection page
      let lensType = "";
      if (currentPath.includes("/collections/polarized")) {
        lensType = "polarized";
      } else if (currentPath.includes("/collections/non-polarized")) {
        lensType = "non-polarized";
      } else if (currentPath.includes("/collections/blue-blocker")) {
        lensType = "low-light";
      } else if (currentPath.includes("/collections/low-light")) {
        lensType = "low-light";
      }

      // Show only products matching the lens type of current collection
      if (lensType && regularProductGrid) {
        const products = regularProductGrid.querySelectorAll(
          ".ombraz-product-v2__grid-item"
        );

        // First show all products to avoid flickering
        products.forEach((product) => {
          product.classList.remove("hide-this");
          product.classList.remove("hide-this-by-filter");
        });

        // Then hide products that don't match the lens type
        requestAnimationFrame(() => {
          products.forEach((product) => {
            const itemLensValue = (
              product.getAttribute("data-lens-value") || ""
            ).toLowerCase();

            let showItem = false;
            if (lensType === "polarized") {
              showItem =
                itemLensValue.includes("polarized") &&
                !itemLensValue.includes("non-polarized");
            } else if (lensType === "non-polarized") {
              showItem = itemLensValue.includes("non-polarized");
            } else if (
              lensType === "blue-blocking" ||
              lensType === "low-light"
            ) {
              showItem =
                itemLensValue.includes("blue-blocking") ||
                itemLensValue.includes("low-light");
            }

            if (showItem) {
              product.classList.remove("hide-this");
              product.classList.remove("hide-this-by-filter");
            } else {
              product.classList.add("hide-this");
              product.classList.add("hide-this-by-filter");
            }
          });
        });
      }
    }

    // Hide "No results" message if it exists
    displayNoResultsMessage(false);
  }

  // Desktop clear all
  if (clearButtonDesktop) {
    clearButtonDesktop.addEventListener("click", function () {
      handleClearAll();
      if (!isMobile) {
        applyFilters();
      }

      // Clear URL parameters
      window.history.replaceState({}, "", window.location.pathname);
    });
  }

  // Mobile clear all
  if (clearButtonMobile) {
    clearButtonMobile.addEventListener("click", function () {
      handleClearAll();
      if (isMobile) {
        applyFilters(); // Reset the view
        closeTheFilterHederMobile();
      }

      // Clear URL parameters
      window.history.replaceState({}, "", window.location.pathname);
    });
  }
}

function handleBestSellerFilter(fromShowResultsButton = false) {
  debugLog("handleBestSellerFilter called", {
    fromShowResultsButton,
    isMobile,
  });

  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  const bestSellerProducts = document.querySelector("[data-custom-products]");
  const saleCheckbox = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  );
  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const saleProducts = document.querySelector("[data-onsale-products]");

  // Get all checked filter values
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  const hasActiveFilters =
    checkedFrameSizes.length > 0 ||
    checkedFrameStyles.length > 0 ||
    checkedLensOptions.length > 0 ||
    checkedFrameColors.length > 0 ||
    checkedGearItems.length > 0;

  if (bestSellerCheckbox?.checked) {
    // Uncheck Sale checkbox when Best Seller is checked
    if (saleCheckbox?.checked) {
      saleCheckbox.checked = false;
    }

    // Hide sale products section completely
    if (saleProducts) {
      saleProducts.classList.add("hide-this", "visually-hidden");
    }

    // Show Best Seller products
    if (bestSellerProducts) {
      bestSellerProducts.classList.remove("hide-this", "visually-hidden");

      // Filter best seller products if other filters are active
      if (hasActiveFilters) {
        const bsItems = bestSellerProducts.querySelectorAll(
          ".bs-product-card-v2__list-item"
        );
        let visibleItemCount = 0;

        // Function to check if a product matches all active filters
        const productMatchesFilters = (product) => {
          let matchesAllFilters = true;

          // Check frame size
          if (checkedFrameSizes.length > 0) {
            const itemSize = (
              product.getAttribute("data-size") || ""
            ).toLowerCase();
            const matchesSize = checkedFrameSizes.some((size) =>
              itemSize.includes(size.toLowerCase())
            );
            if (!matchesSize) matchesAllFilters = false;
          }

          // Check frame style
          if (matchesAllFilters && checkedFrameStyles.length > 0) {
            const itemStyle = (
              product.getAttribute("data-style") || ""
            ).toLowerCase();
            if (!checkedFrameStyles.includes(itemStyle))
              matchesAllFilters = false;
          }

          // Check lens options
          if (matchesAllFilters && checkedLensOptions.length > 0) {
            const itemLensValue = (
              product.getAttribute("data-lens-value") || ""
            ).toLowerCase();
            const matchesLensOption = checkedLensOptions.some((option) => {
              if (option === "polarized") {
                return (
                  itemLensValue.includes("polarized") &&
                  !itemLensValue.includes("non-polarized")
                );
              } else if (option === "non-polarized") {
                return itemLensValue.includes("non-polarized");
              } else if (option === "blue-blocking" || option === "low-light") {
                return (
                  itemLensValue.includes("blue-blocking") ||
                  itemLensValue.includes("low-light")
                );
              }
              return itemLensValue.includes(option);
            });
            if (!matchesLensOption) matchesAllFilters = false;
          }

          // Check frame color
          if (matchesAllFilters && checkedFrameColors.length > 0) {
            const itemColor = (
              product.getAttribute("data-color") || ""
            ).toLowerCase();
            if (!checkedFrameColors.includes(itemColor))
              matchesAllFilters = false;
          }

          return matchesAllFilters;
        };

        // Apply filtering to best seller items
        bsItems.forEach((item) => {
          if (productMatchesFilters(item)) {
            item.classList.remove("hide-this");
            visibleItemCount++;
          } else {
            item.classList.add("hide-this");
          }
        });

        // Show No Results message if no items are visible
        const totalVisibleCount = getTotalVisibleProductCount();
        const showNoResults = totalVisibleCount === 0 && hasActiveFilters;
        displayNoResultsMessage(showNoResults);
      } else {
        // No active filters, show all best seller products
        const bsItems = bestSellerProducts.querySelectorAll(
          ".bs-product-card-v2__list-item"
        );
        bsItems.forEach((item) => item.classList.remove("hide-this"));

        // Make sure No Results message is hidden
        displayNoResultsMessage(false);
      }
    }

    // Hide regular product grid completely
    if (regularProductGrid) {
      regularProductGrid.classList.add("hide-this", "visually-hidden");
    }

    // On mobile, close the filter sidebar
    if (isMobile) {
      closeTheFilterHederMobile();
    }
  } else {
    // Best Seller is unchecked - show regular grid

    // Always hide Best Seller section
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this", "visually-hidden");
    }

    // Show regular product grid
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this", "visually-hidden");
    }

    // If other filters are still active, reapply them
    if (hasActiveFilters) {
      applyFilters();
    } else {
      // No filters active, show all regular products
      const regularProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item"
      );
      regularProducts.forEach((product) =>
        product.classList.remove("hide-this")
      );

      // Hide No results message
      displayNoResultsMessage(false);
    }

    // On mobile, close the filter sidebar
    if (isMobile) {
      closeTheFilterHederMobile();
    }
  }

  // Update UI states
  toggleClearButton(bestSellerCheckbox?.checked || hasActiveFilters);

  // Update URL parameters
  saveFiltersToURL();
}

function handleLimitedEditionFilter(fromShowResultsButton = false) {
  const limitedEditionCheckbox = document.querySelector(
    '.ombraz-limited-edition input[type="checkbox"]'
  );

  if (!limitedEditionCheckbox) {
    debugLog("Limited Edition checkbox not found");
    return;
  }

  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const bestSellerProducts = document.querySelector("[data-custom-products]");
  const saleProducts = document.querySelector("[data-onsale-products]");

  // Check if any other filters are active
  const filterCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"]'
  );
  const hasActiveFilters = Array.from(filterCheckboxes).some(
    (checkbox) => checkbox.checked
  );

  if (limitedEditionCheckbox.checked) {
    debugLog("Limited Edition filter is checked");

    // Hide best seller products
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this", "visually-hidden");
    }

    // Hide sale products
    if (saleProducts) {
      saleProducts.classList.add("hide-this", "visually-hidden");
    }

    // Show only Limited Edition products in regular grid
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this", "visually-hidden");

      const allProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item"
      );

      allProducts.forEach((product) => {
        const isLimitedEdition = product.dataset.limitedEdition === "true";

        if (isLimitedEdition) {
          product.classList.remove("hide-this");
        } else {
          product.classList.add("hide-this");
        }
      });
    }
  } else {
    // LE is unchecked - show normal collection view
    debugLog("Limited Edition filter is unchecked");

    // Always hide Best Seller section
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this", "visually-hidden");
    }

    // Always hide Sale section
    if (saleProducts) {
      saleProducts.classList.add("hide-this", "visually-hidden");
    }

    // Show regular grid - remove LE filtering
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this", "visually-hidden");

      const allProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item"
      );

      // Remove LE-specific hiding - let other filters handle visibility
      allProducts.forEach((product) => {
        product.classList.remove("hide-this");
      });
    }
  }

  // Update UI states
  toggleShowResultsButton(limitedEditionCheckbox.checked || hasActiveFilters);
  toggleClearButton(limitedEditionCheckbox.checked || hasActiveFilters);

  // Update URL parameters
  saveFiltersToURL();
}

function toggleShowResultsButton(show) {
  const showResultsBtn = document.getElementById("show-results");
  if (showResultsBtn) {
    showResultsBtn.classList.toggle("hide-this", !show);
  }
}

function toggleClearButton(show) {
  const clearButtonDesktop = document.getElementById("clear-all");
  const clearButtonMobile = document.getElementById("clear-all-mobile");

  if (clearButtonDesktop) {
    clearButtonDesktop.classList.toggle("hide-this", !show);
  }

  if (clearButtonMobile) {
    clearButtonMobile.classList.toggle("hide-this", !show);
  }
}

function closeTheFilterHederMobile() {
  const sidebar = document.getElementById("ombrazSidebar");
  const filterWrapper = document.getElementById("ombrazFilterWrapper");
  if (sidebar) {
    sidebar.classList.remove("active");
    if (filterWrapper) {
      filterWrapper.classList.remove("is-full-screen");
    }

    // Restore scroll position and unlock body
    document.body.classList.remove("filter-active");
  }
}

function uncheckAllFilters() {
  const allCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"], .ombraz-best-seller input[type="checkbox"]'
  );
  allCheckboxes.forEach((checkbox) => {
    checkbox.checked = false;
  });
}

function handleSaleFilter(fromShowResultsButton = false) {
  debugLog(
    `handleSaleFilter called with fromShowResultsButton=${fromShowResultsButton}`
  );

  const saleCheckbox = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  );
  const saleProducts = document.querySelector("[data-onsale-products]");
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const bestSellerProducts = document.querySelector("[data-custom-products]");

  if (!saleCheckbox || !saleCheckbox.checked) {
    // Hide Sale products section
    if (saleProducts) {
      saleProducts.classList.add("hide-this");
      saleProducts.classList.add("visually-hidden");
    }

    // Show regular product grid
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this");
      regularProductGrid.classList.remove("visually-hidden");
    }

    // Get all checked filter values
    const checkedFrameSizes = getActiveFilters("frame-size");
    const checkedFrameStyles = getActiveFilters("frame-style");
    const checkedLensOptions = getActiveFilters("lens-option");
    const checkedFrameColors = getActiveFilters("frame-color");
    const checkedGearItems = getActiveFilters("gear");

    const hasActiveFilters =
      checkedFrameSizes.length > 0 ||
      checkedFrameStyles.length > 0 ||
      checkedLensOptions.length > 0 ||
      checkedFrameColors.length > 0 ||
      checkedGearItems.length > 0;

    // If other filters are active, reapply them
    if (hasActiveFilters) {
      applyFilters();
    } else if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      // If Best Seller is checked, handle that
      handleBestSellerFilter();
    } else {
      // Show all regular products if no filters are active
      if (regularProductGrid) {
        const regularProducts = regularProductGrid.querySelectorAll(
          ".ombraz-product-v2__grid-item"
        );
        regularProducts.forEach((product) => {
          product.classList.remove("hide-this");
        });
      }

      // Hide "No results" message
      displayNoResultsMessage(false);
    }

    // Update UI states based on the remaining active filters
    const hasAnyFilter =
      hasActiveFilters || (bestSellerCheckbox && bestSellerCheckbox.checked);
    toggleShowResultsButton(hasAnyFilter);
    toggleClearButton(hasAnyFilter);

    // Update URL parameters
    saveFiltersToURL();

    return;
  }

  // For mobile, only show the Show Results button and don't apply filter immediately
  if (isMobile && !fromShowResultsButton) {
    // Only uncheck Best Seller when Sale is checked
    if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      bestSellerCheckbox.checked = false;
      if (bestSellerProducts) {
        bestSellerProducts.classList.add("hide-this");
      }
    }

    // Show "Show Results" button
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Update URL parameters even if we haven't applied the filter yet
    saveFiltersToURL();

    // Exit early - don't apply filter until Show Results is clicked
    return;
  }

  // Only uncheck Best Seller when Sale is checked
  if (bestSellerCheckbox && bestSellerCheckbox.checked) {
    bestSellerCheckbox.checked = false;
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this");
    }
  }

  // Show Sale products section
  if (saleProducts) {
    saleProducts.classList.remove("hide-this");
    saleProducts.classList.remove("visually-hidden");
  }

  // Get all checked filter values
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  const hasActiveFilters =
    checkedFrameSizes.length > 0 ||
    checkedFrameStyles.length > 0 ||
    checkedLensOptions.length > 0 ||
    checkedFrameColors.length > 0 ||
    checkedGearItems.length > 0;

  // Function to check if a product matches all active filters - NO DEDUPLICATION
  const productMatchesFilters = (product) => {
    if (!hasActiveFilters) return true;

    let matchesAllFilters = true;

    // Check frame size
    if (checkedFrameSizes.length > 0) {
      const itemSizes = getAvailableSizes(product);
      const matchesSize = checkedFrameSizes.some((size) => {
        const lowerSize = size.toLowerCase();

        // Enhanced size matching with more flexible comparison
        if (lowerSize === "narrow" || lowerSize === "narrow/regular") {
          return itemSizes.some(
            (s) =>
              s.includes("narrow") || s.includes("n/r") || s.includes("n-r")
          );
        } else if (lowerSize === "regular") {
          return itemSizes.some(
            (s) => s.includes("regular") || s.includes("reg")
          );
        } else if (lowerSize === "xl") {
          return itemSizes.some(
            (s) => s.includes("xl") || s.includes("extra large")
          );
        }

        return itemSizes.includes(lowerSize);
      });

      if (!matchesSize) matchesAllFilters = false;
    }

    // Check frame style
    if (matchesAllFilters && checkedFrameStyles.length > 0) {
      const itemStyle = (
        product.getAttribute("data-style") || ""
      ).toLowerCase();
      if (!checkedFrameStyles.includes(itemStyle)) {
        matchesAllFilters = false;
      }
    }

    // Check lens options
    if (matchesAllFilters && checkedLensOptions.length > 0) {
      const itemLensValue = (
        product.getAttribute("data-lens-value") || ""
      ).toLowerCase();
      const matchesLensOption = checkedLensOptions.some((option) => {
        if (option === "polarized") {
          return (
            itemLensValue.includes("polarized") &&
            !itemLensValue.includes("non-polarized")
          );
        } else if (option === "non-polarized") {
          return itemLensValue.includes("non-polarized");
        } else if (option === "blue-blocking" || option === "low-light") {
          return (
            itemLensValue.includes("blue-blocking") ||
            itemLensValue.includes("low-light")
          );
        }
        return itemLensValue.includes(option);
      });
      if (!matchesLensOption) matchesAllFilters = false;
    }

    // Check frame color
    if (matchesAllFilters && checkedFrameColors.length > 0) {
      const itemColor = (
        product.getAttribute("data-color") || ""
      ).toLowerCase();
      if (!checkedFrameColors.includes(itemColor)) {
        matchesAllFilters = false;
      }
    }

    // Check gear items
    if (matchesAllFilters && checkedGearItems.length > 0) {
      const itemGear = (product.getAttribute("data-gear") || "").toLowerCase();
      if (!checkedGearItems.includes(itemGear)) {
        matchesAllFilters = false;
      }
    }

    return matchesAllFilters;
  };

  // Hide the regular product grid completely
  if (regularProductGrid) {
    regularProductGrid.classList.add("hide-this");
    regularProductGrid.classList.add("visually-hidden");
  }

  // Filter only sale products section - NO DEDUPLICATION
  let visibleCount = 0;
  if (saleProducts) {
    const saleItems = saleProducts.querySelectorAll(".is-onsale-product-card");
    saleItems.forEach((product) => {
      if (productMatchesFilters(product)) {
        product.classList.remove("hide-this");
        visibleCount++;
      } else {
        product.classList.add("hide-this");
      }
    });
  }

  // Show or hide "No results" message using total count across all containers
  const totalVisibleCount = getTotalVisibleProductCount();
  debugLog(
    `handleSaleFilter: Total visible products across all containers: ${totalVisibleCount}`
  );
  displayNoResultsMessage(totalVisibleCount === 0 && hasActiveFilters);

  // Update UI states
  toggleShowResultsButton(true);
  toggleClearButton(true);

  // Update URL parameters
  saveFiltersToURL();
}

function toggleFilter() {
  const sidebar = document.getElementById("ombrazSidebar");
  const filterWrapper = document.getElementById("ombrazFilterWrapper");

  if (sidebar) {
    const isActive = sidebar.classList.contains("active");

    if (!isActive) {
      // Opening the filter
      sidebar.classList.add("active");
      if (filterWrapper) {
        filterWrapper.classList.add("is-full-screen");
      }

      // Store current scroll position and lock body
      document.body.classList.add("filter-active");
    } else {
      // Closing the filter
      closeTheFilterHederMobile();
    }
  }
}

// Function to query and filter products by size from URL parameter
function queryFilterBySize(size) {
  debugLog(`Filtering by size: ${size}`);

  // Normalize the size value
  const normalizedSize = size.toLowerCase().trim();

  // Get all product cards
  const productCards = document.querySelectorAll(
    ".ombraz-product-v2__grid-item"
  );

  // Track if we've found the checkbox for this size
  let foundCheckbox = false;

  // Find and check the corresponding size checkbox
  const sizeCheckbox = document.querySelector(
    `input[type="checkbox"][data-filter-type="frame-size"][data-frame-size="${normalizedSize}"]`
  );

  if (sizeCheckbox) {
    foundCheckbox = true;
    debugLog(`Found checkbox for size: ${normalizedSize}`);

    // Open the accordion containing this checkbox
    const accordion = sizeCheckbox.closest(".ombraz-accordion-item");
    if (accordion) {
      accordion.setAttribute("open", "");
      updateAccordionIcon(accordion, true);
    }

    // Check the checkbox
    sizeCheckbox.checked = true;

    // Don't trigger change event yet - we'll apply all filters at once
  } else {
    debugLog(`Could not find checkbox for size: ${normalizedSize}`);
  }

  // If we didn't find a checkbox, we'll manually filter the products
  if (!foundCheckbox) {
    debugLog(`Manually filtering products by size: ${normalizedSize}`);

    productCards.forEach((card) => {
      // Use our improved size detection function
      const itemSizes = getAvailableSizes(card);
      const hasSize = itemSizes.includes(normalizedSize);

      // Check if this is a gear/accessory item (which don't have sizes)
      const isGearOrAccessory =
        card.getAttribute("data-style")?.includes("shields") ||
        card.getAttribute("data-style")?.includes("nose-shield") ||
        card.getAttribute("data-style")?.includes("riser") ||
        card.getAttribute("data-gear")?.includes("shields") ||
        card.getAttribute("data-gear")?.includes("risers") ||
        card.getAttribute("data-gear")?.includes("case") ||
        card.getAttribute("data-gear")?.includes("floatie") ||
        card.getAttribute("data-gear")?.includes("cordreplacement") ||
        card.getAttribute("data-gear")?.includes("sticker") ||
        card.getAttribute("data-gear")?.includes("giftcard");

      // Hide items that don't match the size or are gear items
      if (isGearOrAccessory || !hasSize) {
        card.classList.add("hide-this");
      }
    });
  }

  return foundCheckbox;
}

// Helper function to reset visibility of all products in the grid
function resetAllProductVisibility() {
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  if (productGrid) {
    const products = productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item"
    );
    products.forEach((product) => {
      product.classList.remove("hide-this");
      product.classList.remove("hide-this-by-filter");
    });
  }
}

// Function to handle direct size URL parameters
function handleSizeURLParameter() {
  const urlParams = new URLSearchParams(window.location.search);

  // Check if the sizes parameter exists
  if (urlParams.has("sizes")) {
    const sizeValues = urlParams.get("sizes").split(",");
    debugLog("Handling direct size URL parameter with values:", sizeValues);

    // Process each size value
    let foundAnySize = false;

    sizeValues.forEach((size) => {
      const normalizedSize = size.toLowerCase().trim();

      // Only process valid sizes
      if (["narrow", "regular", "xl"].includes(normalizedSize)) {
        // Try to find and check the corresponding checkbox
        const found = queryFilterBySize(normalizedSize);
        foundAnySize = foundAnySize || found;
      }
    });

    // If we found and checked any size checkboxes, apply the filters
    if (foundAnySize) {
      // Show filter UI elements
      toggleShowResultsButton(true);
      toggleClearButton(true);

      // Apply filters
      applyFilters();

      return true; // Indicate that we handled the parameter
    }
  }

  return false; // Indicate that we didn't handle any parameter
}
